import { apiFetch } from './client';

// #296 — Customer API → Logins: the human accounts that sign in to the /apply/
// submission portal, as opposed to the machine credentials in intakeClients.ts.
// bcid-admin only, and like the intake-client routes these 503 with "customer
// API management not configured" until bcid-admin-svc is provisioned.
//
// Disabling is the routine offboarding action and preserves the audit history,
// which is most of the reason the portal requires a login at all. Since #463
// a disabled login can also be DELETED — the Keycloak account is destroyed and
// the local record tombstoned, so that history survives either way.

/**
 * How a login signs in (#394), chosen when it is provisioned.
 *
 * `local` — Keycloak holds the password and the TOTP secret; the account is
 *   activated by an invite email.
 * `sso` — the person authenticates through their own Microsoft or Google
 *   account. No local password and no Keycloak TOTP, so the invite and
 *   reset-MFA actions do not apply and the API refuses them with a 409.
 *
 * Derived server-side from account state rather than stored, so it is read-only
 * on an existing login: there is no route that changes it.
 */
export type AuthMode = 'local' | 'sso';

/**
 * What a portal login may do (#400), carried as a Keycloak realm role beside
 * `customer-portal-user`.
 *
 * `viewer` — read-only. Can see the company's submissions, TN change requests
 *   and registration status; cannot create, edit or withdraw any of them.
 * `submitter` — everything a viewer can, plus submitting, editing and
 *   withdrawing submissions and raising TN change requests.
 * `admin` — everything a submitter can, plus managing their own company's
 *   logins (#403).
 *
 * Unlike `authMode`, this IS changeable on an existing login: see
 * `setCustomerUserTier`.
 */
export type CustomerTier = 'viewer' | 'submitter' | 'admin';

/** Display order, widest last — used for the tier picker so it reads as a ladder. */
export const CUSTOMER_TIERS: CustomerTier[] = ['viewer', 'submitter', 'admin'];

export const TIER_LABELS: Record<CustomerTier, { text: string; hint: string }> = {
  viewer:    { text: 'Viewer',    hint: 'read-only' },
  submitter: { text: 'Submitter', hint: 'can submit, edit and withdraw' },
  admin:     { text: 'Admin',     hint: 'submitter, plus manages their own logins' },
};

/**
 * How a login's `tiers` array reads on screen.
 *
 * The API returns the tier roles the account ACTUALLY holds rather than a
 * single derived value, because the two off-contract states are real and are
 * invisible everywhere else: a login with none cannot sign in (every account
 * provisioned before #400 is in this state until staff assign one), and a login
 * with more than one is refused as ambiguous. Both come back from the same
 * token guard as a bare 401, so this page is where an operator can actually see
 * which it is — and the fix for both is the same, set the tier.
 */
export function tierLabel(tiers: CustomerTier[]): { text: string; hint: string; ok: boolean } {
  if (tiers.length === 1) return { ...TIER_LABELS[tiers[0]], ok: true };
  if (tiers.length === 0) {
    return {
      text: 'No access level',
      hint: 'cannot sign in until one is set',
      ok: false,
    };
  }
  // Render the conflicting pair in CUSTOMER_TIERS order, not the order the API
  // happened to send. The API's order falls out of the key order of
  // PORTAL_TIER_ROLES in the server's auth/principals.ts (admin, submitter,
  // viewer) — an implementation detail nothing on this side should inherit, and
  // one that would silently change this badge's text if anyone reordered that
  // object. Sorting into the display ladder makes the text a function of WHICH
  // tiers are held rather than of how they arrived.
  const ordered = CUSTOMER_TIERS.filter((t) => tiers.includes(t));
  return {
    text: 'Conflicting',
    hint: `holds ${ordered.map((t) => TIER_LABELS[t].text).join(' + ')} — sign-in is refused`,
    ok: false,
  };
}

export interface CustomerUser {
  keycloakId: string;
  username: string;
  email: string | null;
  name: string;
  customerId: string | null;
  enabled: boolean;
  /** Outstanding Keycloak required actions, e.g. UPDATE_PASSWORD, CONFIGURE_TOTP. */
  pendingActions: string[];
  authMode: AuthMode;
  /**
   * Every tier role the account holds. Exactly one is the contract; see
   * {@link tierLabel} for why the other two lengths are surfaced rather than
   * collapsed.
   */
  tiers: CustomerTier[];
}

export async function listCustomerUsers(customerId?: string): Promise<CustomerUser[]> {
  const qs = customerId ? `?customerId=${encodeURIComponent(customerId)}` : '';
  const res = await apiFetch<{ items: CustomerUser[] }>(`/admin/customer-users${qs}`);
  return res.items;
}

export interface CreateCustomerUserInput {
  email: string;
  customerId: string;
  /**
   * Required, with no default (#400). The API 400s a create that omits it,
   * deliberately: whichever value a default picked, it would be a capability
   * decision nobody made.
   */
  tier: CustomerTier;
  firstName?: string;
  lastName?: string;
  /** Omitted means `local`, which is what this route did before #394. */
  authMode?: AuthMode;
}

export interface CreateCustomerUserResult {
  keycloakId: string;
  username: string;
  customerId: string;
  tier: CustomerTier;
  authMode: AuthMode;
  /**
   * `sso` creates only, and present on every one of them so its absence can
   * never be mistaken for success. `false` means the account is live but nobody
   * has been told — the welcome email could not be queued. The create still
   * succeeds, because an SSO login needs no email in order to work and has no
   * invite to re-send; the operator has to pass on the portal URL instead.
   *
   * Omitted for `local` creates: there a mail failure is a 502 rather than a
   * 201, so the field could only ever be `true`.
   */
  welcomeEmailQueued?: boolean;
}

export async function createCustomerUser(
  input: CreateCustomerUserInput,
  idempotencyKey: string,
): Promise<CreateCustomerUserResult> {
  return apiFetch<CreateCustomerUserResult>('/admin/customer-users', {
    method: 'POST',
    headers: { 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify(input),
  });
}

export async function setCustomerUserEnabled(keycloakId: string, enabled: boolean): Promise<void> {
  await apiFetch<{ ok: boolean }>(`/admin/customer-users/${encodeURIComponent(keycloakId)}/enabled`, {
    method: 'PUT',
    body: JSON.stringify({ enabled }),
  });
}

/**
 * Delete an account outright (#463).
 *
 * Destroys the Keycloak account — login, credentials, sessions, federated links
 * and role memberships. The target must already be DISABLED; the API refuses an
 * enabled one with 409, because deleting cannot be undone and should not be one
 * click away from a working account.
 *
 * What survives, and the reason the local record is tombstoned rather than
 * removed: their case assignments and every audit entry naming them keep
 * rendering a name.
 */
export async function deleteCustomerUser(keycloakId: string): Promise<void> {
  await apiFetch<{ ok: boolean }>(`/admin/customer-users/${encodeURIComponent(keycloakId)}`, {
    method: 'DELETE',
  });
}

/**
 * Set a login's tier to exactly `tier`, replacing whatever it held (#400).
 * Also the repair for a login with no tier or with more than one — the write is
 * "make it exactly this", not "add this".
 *
 * Returns the tiers held beforehand so the caller can say what actually
 * changed; an empty `from` means the login had no access level at all.
 */
export async function setCustomerUserTier(
  keycloakId: string,
  tier: CustomerTier,
): Promise<{ from: CustomerTier[]; to: CustomerTier }> {
  return apiFetch<{ ok: boolean; from: CustomerTier[]; to: CustomerTier }>(
    `/admin/customer-users/${encodeURIComponent(keycloakId)}/tier`,
    { method: 'PUT', body: JSON.stringify({ tier }) },
  );
}

export async function resendCustomerUserInvite(keycloakId: string): Promise<void> {
  await apiFetch<{ ok: boolean }>(`/admin/customer-users/${encodeURIComponent(keycloakId)}/invite`, {
    method: 'POST',
  });
}

export async function resetCustomerUserTotp(keycloakId: string): Promise<void> {
  await apiFetch<{ ok: boolean }>(`/admin/customer-users/${encodeURIComponent(keycloakId)}/reset-totp`, {
    method: 'POST',
  });
}
